import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { MoreHorizontal, MessageCircle, Trash2, Flag } from 'lucide-react';
import { Avatar } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  deletePost,
  upsertReaction,
  deleteReaction,
  createComment,
  deleteComment,
  listComments,
} from '@/lib/api/posts';
import type { Post, Reaction, Comment, ReactionKind } from '@/lib/api/posts';
import type { LinkPreview } from '@/lib/api/links';
import { useAuth } from '@/lib/hooks/useAuth';
import { ReportPostDialog } from './ReportPostDialog';
import { LinkPreviewCard } from './LinkPreviewCard';
import { ImageLightbox } from './ImageLightbox';
import { OnlineIndicator } from '@/components/profile/OnlineIndicator';
import { cn } from '@/lib/utils/cn';

function castLinkPreview(raw: unknown): LinkPreview | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return null;
  const p = raw as Record<string, unknown>;
  if (typeof p.url !== 'string') return null;
  return p as unknown as LinkPreview;
}

type ProfileSnippet = {
  id: string;
  display_name: string;
  handle: string | null;
  avatar_url: string | null;
  headline: string | null;
  last_seen_at?: string | null;
};

const REACTION_EMOJI: Record<ReactionKind, string> = {
  like: '👍',
  insightful: '💡',
  celebrate: '🎉',
  support: '🤝',
};

type Props = {
  post: Post;
  author: ProfileSnippet;
  reactions: Reaction[];
  commentCount: number;
};

export function PostCard({ post, author, reactions, commentCount }: Props) {
  const { profile } = useAuth();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [showComments, setShowComments] = useState(false);
  const [commentText, setCommentText] = useState('');
  const [reportOpen, setReportOpen] = useState(false);
  const bodyRef = useRef<HTMLParagraphElement>(null);
  const [isClamped, setIsClamped] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightboxIndex, setLightboxIndex] = useState(0);

  useEffect(() => {
    const el = bodyRef.current;
    if (el) setIsClamped(el.scrollHeight > el.clientHeight);
  }, [post.content]);

  const myReaction = profile
    ? reactions.find((r) => r.author_id === profile.id)?.kind ?? null
    : null;

  const reactionCounts = reactions.reduce<Partial<Record<ReactionKind, number>>>(
    (acc, r) => ({ ...acc, [r.kind]: (acc[r.kind] ?? 0) + 1 }),
    {},
  );

  const { data: comments = [] } = useQuery({
    queryKey: ['comments', post.id],
    queryFn: () => listComments(post.id),
    enabled: showComments,
  });

  const { mutate: reactMutate } = useMutation<void, Error, ReactionKind>({
    mutationFn: async (kind) => {
      if (!profile) throw new Error('not authenticated');
      if (myReaction === kind) {
        await deleteReaction(post.id, profile.id);
      } else {
        await upsertReaction(post.id, profile.id, kind);
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['feed'] }),
  });

  const { mutate: removeMutate } = useMutation({
    mutationFn: () => deletePost(post.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['feed'] }),
  });

  const { mutate: addComment, isPending: commenting } = useMutation({
    mutationFn: () => {
      if (!profile) return Promise.reject(new Error('not authenticated'));
      return createComment(post.id, profile.id, commentText.trim());
    },
    onSuccess: () => {
      setCommentText('');
      qc.invalidateQueries({ queryKey: ['comments', post.id] });
    },
  });

  const { mutate: removeComment } = useMutation({
    mutationFn: (id: string) => deleteComment(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['comments', post.id] }),
  });

  const isOwn = profile?.id === post.author_id;

  return (
    <>
    <Card className="mb-2 border-muted/30">
      <CardContent className="pt-3 pb-1">
        {/* Header */}
        <div className="flex items-start justify-between mb-2">
          <div className="flex gap-3">
            <div className="relative shrink-0">
              <Link to={author.handle ? `/profile/${author.handle}` : '#'}>
                <Avatar src={author.avatar_url} name={author.display_name} size="md" />
              </Link>
              <OnlineIndicator profileId={author.id} lastSeenAt={author.last_seen_at} />
            </div>
            <div>
              <Link
                to={author.handle ? `/profile/${author.handle}` : '#'}
                className="font-semibold text-navy hover:underline text-sm"
              >
                {author.display_name}
              </Link>
              {author.headline && (
                <p className="text-xs text-muted line-clamp-1">{author.headline}</p>
              )}
              <p className="text-xs text-muted">
                {formatDistanceToNow(new Date(post.created_at), { addSuffix: true })}
              </p>
            </div>
          </div>
          {profile && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="h-8 w-8">
                  <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
                  <span className="sr-only">Post options</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {isOwn && (
                  <DropdownMenuItem
                    className="text-red-600 focus:text-red-600"
                    onClick={() => removeMutate()}
                  >
                    <Trash2 className="mr-2 h-4 w-4" aria-hidden="true" />
                    Delete post
                  </DropdownMenuItem>
                )}
                {!isOwn && (
                  <DropdownMenuItem onClick={() => setReportOpen(true)}>
                    <Flag className="mr-2 h-4 w-4" aria-hidden="true" />
                    Report post
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>

        {/* Content */}
        <p
          ref={bodyRef}
          className={cn(
            'text-sm text-ink whitespace-pre-wrap',
            !isExpanded && 'line-clamp-2',
            isClamped || isExpanded ? 'mb-1' : 'mb-3',
          )}
        >
          {post.content}
        </p>
        {(isClamped || isExpanded) && (
          <button
            onClick={() => setIsExpanded((p) => !p)}
            className="mb-2 text-xs text-muted hover:text-navy transition-colors"
          >
            {isExpanded ? 'Show less' : 'Read more'}
          </button>
        )}

        {/* Tags — click any badge to filter the feed */}
        <div className="flex flex-wrap gap-1.5 mb-3">
          <button
            onClick={() => navigate(`/feed?topic=${encodeURIComponent(post.topic)}`)}
            className="hover:opacity-75 transition-opacity"
          >
            <Badge variant="default">{post.topic}</Badge>
          </button>
          <button
            onClick={() => navigate(`/feed?industry=${encodeURIComponent(post.industry)}`)}
            className="hover:opacity-75 transition-opacity"
          >
            <Badge variant="outline">{post.industry}</Badge>
          </button>
          {post.location && (
            <button
              onClick={() => navigate(`/feed?location=${encodeURIComponent(post.location!)}`)}
              className="hover:opacity-75 transition-opacity"
            >
              <Badge variant="gold">{post.location}</Badge>
            </button>
          )}
        </div>

        {/* Link preview */}
        {castLinkPreview(post.link_preview) && (
          <div className="mb-3">
            <LinkPreviewCard preview={castLinkPreview(post.link_preview)!} />
          </div>
        )}

        {/* Media */}
        {post.media_urls.length > 0 && (
          <div
            className={cn(
              'grid gap-1 mb-3 rounded-lg overflow-hidden',
              post.media_urls.length === 1 ? 'grid-cols-1' : 'grid-cols-2',
            )}
          >
            {post.media_urls.map((url, index) => (
              <button
                key={url}
                type="button"
                onClick={() => { setLightboxIndex(index); setLightboxOpen(true); }}
                className="relative group block overflow-hidden focus-visible:ring-2 focus-visible:ring-gold/50 rounded"
                aria-label={`View image ${index + 1} of ${post.media_urls.length} at full size`}
              >
                <img src={url} alt="" className="w-full object-cover max-h-80 transition-transform duration-150 group-hover:scale-[1.02]" />
                <span className="absolute inset-0 bg-ink/0 group-hover:bg-ink/10 transition-colors" aria-hidden="true" />
              </button>
            ))}
          </div>
        )}

        {/* Reaction summary */}
        {reactions.length > 0 && (
          <div className="flex items-center gap-1 mb-2 pb-2 border-b border-muted/20">
            {(Object.keys(REACTION_EMOJI) as ReactionKind[])
              .filter((k) => reactionCounts[k])
              .map((k) => (
                <span key={k} className="text-sm">{REACTION_EMOJI[k]}</span>
              ))}
            <span className="text-xs text-muted ml-1">{reactions.length}</span>
            {commentCount > 0 && (
              <span className="ml-auto text-xs text-muted">
                {commentCount} comment{commentCount !== 1 ? 's' : ''}
              </span>
            )}
          </div>
        )}

        {/* Action bar */}
        {profile && (
          <div className="flex gap-1 pt-1">
            {(Object.keys(REACTION_EMOJI) as ReactionKind[]).map((kind) => (
              <button
                key={kind}
                onClick={() => reactMutate(kind)}
                className={cn(
                  'flex items-center gap-1 rounded px-2 py-1 text-xs transition-colors',
                  myReaction === kind
                    ? 'bg-gold/20 text-gold-dark font-semibold'
                    : 'text-muted hover:bg-cream-warm',
                )}
                title={kind.charAt(0).toUpperCase() + kind.slice(1)}
              >
                <span>{REACTION_EMOJI[kind]}</span>
                {reactionCounts[kind] ? <span>{reactionCounts[kind]}</span> : null}
              </button>
            ))}
            <button
              onClick={() => setShowComments((p) => !p)}
              className="ml-auto flex items-center gap-1 rounded px-2 py-1 text-xs text-muted hover:bg-cream-warm transition-colors"
            >
              <MessageCircle className="h-3.5 w-3.5" aria-hidden="true" />
              <span>Comment</span>
            </button>
          </div>
        )}

        {/* Comments */}
        {showComments && (
          <div className="mt-3 space-y-3 border-t border-muted/20 pt-3">
            {comments.map((c) => (
              <CommentRow
                key={c.id}
                comment={c}
                isOwn={profile?.id === c.author_id}
                onDelete={() => removeComment(c.id)}
              />
            ))}
            {profile && (
              <div className="flex gap-2">
                <Textarea
                  value={commentText}
                  onChange={(e) => setCommentText(e.target.value)}
                  placeholder="Add a comment…"
                  className="min-h-[60px] resize-none text-sm border-muted/30"
                  maxLength={1000}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && commentText.trim()) {
                      addComment();
                    }
                  }}
                />
                <Button
                  size="sm"
                  className="self-end bg-navy text-cream hover:bg-navy-deep"
                  disabled={!commentText.trim() || commenting}
                  onClick={() => addComment()}
                >
                  Post
                </Button>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
    {profile && !isOwn && (
      <ReportPostDialog postId={post.id} open={reportOpen} onOpenChange={setReportOpen} />
    )}
    {post.media_urls.length > 0 && (
      <ImageLightbox
        images={post.media_urls}
        initialIndex={lightboxIndex}
        open={lightboxOpen}
        onOpenChange={setLightboxOpen}
      />
    )}
    </>
  );
}

function CommentRow({
  comment,
  isOwn,
  onDelete,
}: {
  comment: Comment;
  isOwn: boolean;
  onDelete: () => void;
}) {
  return (
    <div className="flex gap-2 text-sm">
      <div className="flex-1 rounded-lg bg-cream-warm/60 px-3 py-2">
        <p className="text-ink">{comment.content}</p>
        <p className="mt-0.5 text-xs text-muted">
          {formatDistanceToNow(new Date(comment.created_at), { addSuffix: true })}
        </p>
      </div>
      {isOwn && (
        <button
          onClick={onDelete}
          className="self-start mt-1 text-muted hover:text-red-500 transition-colors"
          aria-label="Delete comment"
        >
          <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
        </button>
      )}
    </div>
  );
}
